import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import Topbar from "../../components/topbar/Topbar";
import toast from "react-hot-toast";
import "./adminmusic.css";

const MOODS = [
  "dramatic", "uplifting", "comedic", "tense", "chill",
  "epic", "emotional", "dark",
  "hopeful", "love", "sexy", "motivating", "mystery",
  "scary", "storytelling", "upbeat",
];
const ENERGY = ["low", "medium", "high"];

// `moods` is now an array — a track can belong to multiple playlists
// (e.g. "Iris" is both hopeful AND love). Form keeps a Set internally
// and serializes to array on submit.
const EMPTY_FORM = {
  trackName: "",
  artist: "",
  moods: ["uplifting"],
  energy: "medium",
  tags: "",
  tiktokSoundUrl: "",
  spotifyUrl: "",
};

export default function AdminMusic() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [filterMood, setFilterMood] = useState("");
  const [filterEnergy, setFilterEnergy] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    document.title = "Clipbait — Music Library";
  }, []);

  useEffect(() => {
    if (!user) return;
    loadTracks();
  }, [user, filterMood, filterEnergy, search]);

  async function loadTracks() {
    setLoading(true);
    try {
      const params = {};
      if (filterMood)   params.mood = filterMood;
      if (filterEnergy) params.energy = filterEnergy;
      if (search.trim()) params.q = search.trim();
      const { data } = await axiosInstance.get("/admin/music", { params });
      setTracks(data.tracks || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin only");
        navigate("/");
        return;
      }
      toast.error(err.response?.data?.error || "Failed to load tracks");
    } finally {
      setLoading(false);
    }
  }

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingId(null);
  }

  function startEdit(track) {
    setEditingId(track._id);
    setForm({
      trackName: track.trackName || "",
      artist: track.artist || "",
      moods: track.moods && track.moods.length > 0 ? track.moods : ["uplifting"],
      energy: track.energy,
      tags: (track.tags || []).join(", "),
      tiktokSoundUrl: track.tiktokSoundUrl || "",
      spotifyUrl: track.spotifyUrl || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleMood(mood) {
    setForm((f) => {
      const has = f.moods.includes(mood);
      const next = has ? f.moods.filter((m) => m !== mood) : [...f.moods, mood];
      // Don't let user clear the last mood — the backend requires ≥1.
      return { ...f, moods: next.length > 0 ? next : f.moods };
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.trackName.trim()) {
      toast.error("Track name required");
      return;
    }
    if (form.moods.length === 0) {
      toast.error("Pick at least one mood");
      return;
    }
    const payload = {
      trackName: form.trackName,
      artist: form.artist,
      moods: form.moods,
      energy: form.energy,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      tiktokSoundUrl: form.tiktokSoundUrl,
      spotifyUrl: form.spotifyUrl,
    };
    try {
      if (editingId) {
        await axiosInstance.patch(`/admin/music/${editingId}`, payload);
        toast.success("Track updated");
      } else {
        await axiosInstance.post("/admin/music", payload);
        toast.success("Track added");
      }
      resetForm();
      loadTracks();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save");
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this track?")) return;
    try {
      await axiosInstance.delete(`/admin/music/${id}`);
      toast.success("Deleted");
      loadTracks();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  }

  return (
    <>
      <Topbar />
      <div className="admin-music-page">
        <div className="admin-music-container">
          <h1 className="admin-music-title">Music Library</h1>
          <p className="admin-music-sub">
            Tracks you add here power the per-clip recommendations.
          </p>

          {/* Add/Edit form */}
          <form className="admin-music-form" onSubmit={submit}>
            <h2>{editingId ? "Edit track" : "Add new track"}</h2>

            <div className="admin-music-row">
              <label>
                Track name *
                <input
                  type="text"
                  value={form.trackName}
                  onChange={(e) => update("trackName", e.target.value)}
                  placeholder="e.g. Cinematic Rise"
                  required
                />
              </label>
              <label>
                Artist
                <input
                  type="text"
                  value={form.artist}
                  onChange={(e) => update("artist", e.target.value)}
                  placeholder="optional"
                />
              </label>
            </div>

            <div className="admin-music-full">
              <span className="admin-music-field-label">Moods * <span className="admin-music-hint">(pick all that fit — a track can live in multiple playlists)</span></span>
              <div className="admin-music-mood-chips">
                {MOODS.map((m) => {
                  const on = form.moods.includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      className={`admin-music-mood-chip${on ? " on" : ""}`}
                      onClick={() => toggleMood(m)}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="admin-music-row">
              <label>
                Energy *
                <select value={form.energy} onChange={(e) => update("energy", e.target.value)}>
                  {ENERGY.map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
              </label>
            </div>

            <label className="admin-music-full">
              Tags (comma-separated)
              <input
                type="text"
                value={form.tags}
                onChange={(e) => update("tags", e.target.value)}
                placeholder="e.g. lo-fi, trap, 8-bit"
              />
            </label>

            <div className="admin-music-row">
              <label>
                TikTok sound URL
                <input
                  type="url"
                  value={form.tiktokSoundUrl}
                  onChange={(e) => update("tiktokSoundUrl", e.target.value)}
                  placeholder="https://www.tiktok.com/music/..."
                />
              </label>
              <label>
                Spotify URL
                <input
                  type="url"
                  value={form.spotifyUrl}
                  onChange={(e) => update("spotifyUrl", e.target.value)}
                  placeholder="https://open.spotify.com/track/..."
                />
              </label>
            </div>

            <div className="admin-music-actions">
              <button type="submit" className="admin-music-btn-primary">
                {editingId ? "Save changes" : "Add track"}
              </button>
              {editingId && (
                <button type="button" className="admin-music-btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>

          {/* Filters */}
          <div className="admin-music-filters">
            <input
              type="text"
              placeholder="Search by track or artist..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={filterMood} onChange={(e) => setFilterMood(e.target.value)}>
              <option value="">All moods</option>
              {MOODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={filterEnergy} onChange={(e) => setFilterEnergy(e.target.value)}>
              <option value="">All energy</option>
              {ENERGY.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
            <span className="admin-music-count">{tracks.length} tracks</span>
          </div>

          {/* Track list */}
          {loading ? (
            <div className="admin-music-empty">Loading…</div>
          ) : tracks.length === 0 ? (
            <div className="admin-music-empty">No tracks yet. Add your first above.</div>
          ) : (
            <table className="admin-music-table">
              <thead>
                <tr>
                  <th>Track</th>
                  <th>Artist</th>
                  <th>Moods</th>
                  <th>Energy</th>
                  <th>Tags</th>
                  <th>Links</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tracks.map((t) => (
                  <tr key={t._id}>
                    <td>{t.trackName}</td>
                    <td>{t.artist || <span className="muted">—</span>}</td>
                    <td>
                      {(t.moods || []).map((m) => (
                        <span key={m} className={`mood-chip mood-${m}`}>{m}</span>
                      ))}
                    </td>
                    <td><span className={`energy-chip energy-${t.energy}`}>{t.energy}</span></td>
                    <td>
                      {(t.tags || []).length > 0
                        ? t.tags.map((tag) => <span key={tag} className="tag-chip">{tag}</span>)
                        : <span className="muted">—</span>}
                    </td>
                    <td>
                      {t.tiktokSoundUrl && <a href={t.tiktokSoundUrl} target="_blank" rel="noreferrer">TT</a>}
                      {t.spotifyUrl && <a href={t.spotifyUrl} target="_blank" rel="noreferrer">SP</a>}
                    </td>
                    <td className="admin-music-row-actions">
                      <button onClick={() => startEdit(t)}>Edit</button>
                      <button onClick={() => remove(t._id)} className="danger">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
